import React, { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";
import { Widget } from "./analyticsState";

interface Props {
  widget: Widget;
  isDark: boolean;
}

const ChartRenderer: React.FC<Props> = ({ widget, isDark }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<ApexCharts | null>(null);
  const prevType = useRef<string>(widget.type);

  const { type, data, uiConfig, colors } = widget;
  const { axes, specifics } = uiConfig;

  useEffect(() => {
    if (!chartRef.current) return;

    const textColor = isDark ? "#d0d0d0" : "#5F6368";
    const gridColor = isDark ? "#2a3a5a" : "#E8EAED";

    const options: any = {
      chart: {
        type: getApexType(type),
        height: "100%",
        width: "100%",
        toolbar: { show: false },
        background: "transparent",
        fontFamily: "Inter, sans-serif",
        animations: { enabled: true, easing: "easeinout", speed: 400 },
      },
      colors: colors.slice(0, Math.max(data.series.length, data.labels.length)),
      theme: { mode: isDark ? "dark" : "light" },
      grid: {
        show: axes.showGridlines,
        borderColor: gridColor,
        strokeDashArray: 3,
      },
      dataLabels: { enabled: specifics.showDataLabels || false },
      legend: {
        show: specifics.showLegend !== false,
        position: specifics.legendPosition || "bottom",
        labels: { colors: textColor },
      },
      tooltip: { theme: isDark ? "dark" : "light" },
    };

    // Construct chart-specific options
    applyTypeSpecificOptions(type, data, axes, specifics, textColor, colors, options);

    if (type === "scorecard" || type === "table" || type === "text" || type === "image") {
       if (chartInstance.current) {
         chartInstance.current.destroy();
         chartInstance.current = null;
       }
       return;
    }

    if (chartInstance.current && prevType.current === type) {
      // Smooth update
      chartInstance.current.updateOptions(options);
    } else {
      // Full re-render if type changed or first render
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
      prevType.current = type;
    }

    return () => {
      // We don't destroy on every effect run anymore, only on unmount
    };
  }, [widget, isDark]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  if (type === "scorecard") {
    return (
      <div className="ca-scorecard-widget">
        <div className="ca-scorecard-value" style={{ color: colors[0] || "#1A73E8" }}>
          {specifics.scorecardPrefix}{specifics.scorecardValue}{specifics.scorecardSuffix}
        </div>
        <div className="ca-scorecard-label">{specifics.scorecardLabel}</div>
      </div>
    );
  }

  if (type === "table") {
    return (
      <div className="ca-table-widget">
        <table className={`ca-table ${isDark ? "ca-dark" : ""}`}>
          <thead>
            <tr>
              <th>Category</th>
              {data.series.map((s: any) => <th key={s.name}>{s.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.labels.map((l, i) => (
              <tr key={i}>
                <td>{l}</td>
                {data.series.map((s: any) => <td key={s.name}>{s.data[i]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "text") {
    return (
      <div className="ca-text-widget" style={{ color: isDark ? "#E8EAED" : "#3C4043" }}>
        {specifics.textContent || "No text content"}
      </div>
    );
  }

  if (type === "image") {
    if (!specifics.imageUrl) {
      return (
        <div className="ca-image-widget">
          <div style={{ textAlign: "center" }}>
            <i className="fa fa-image" style={{ fontSize: 32, opacity: 0.3 }} />
            <p style={{ fontSize: 12, marginTop: 8 }}>Set image URL in sidebar</p>
          </div>
        </div>
      );
    }
    return (
      <div className="ca-image-widget">
        <img src={specifics.imageUrl} alt="Widget" />
      </div>
    );
  }

  return <div ref={chartRef} style={{ width: "100%", height: "100%" }} />;
};

function applyTypeSpecificOptions(type: string, data: any, axes: any, specifics: any, textColor: string, colors: string[], options: any) {
    switch (type) {
      case "bar": {
        const horizontal = specifics.orientation === "horizontal";
        options.series = data.series;
        options.xaxis = {
          categories: data.labels,
          labels: { style: { colors: textColor, fontSize: "11px" } },
          title: { text: axes.xAxisTitle || undefined, style: { color: textColor, fontSize: "12px" } },
          axisBorder: { show: axes.showXAxis },
          axisTicks: { show: axes.showXAxis },
        };
        options.yaxis = {
          labels: { style: { colors: textColor, fontSize: "11px" } },
          title: { text: axes.yAxisTitle || undefined, style: { color: textColor, fontSize: "12px" } },
        };
        options.plotOptions = {
          bar: {
            horizontal,
            borderRadius: specifics.barBorderRadius || 4,
            columnWidth: "60%",
          },
        };
        options.chart.stacked = specifics.stacked || false;
        break;
      }
      case "line":
      case "area": {
        options.series = data.series;
        options.xaxis = {
          categories: data.labels,
          labels: { style: { colors: textColor, fontSize: "11px" } },
          title: { text: axes.xAxisTitle || undefined, style: { color: textColor } },
        };
        options.yaxis = {
          labels: { style: { colors: textColor, fontSize: "11px" } },
          title: { text: axes.yAxisTitle || undefined, style: { color: textColor } },
        };
        options.stroke = {
          curve: specifics.smooth ? "smooth" : "straight",
          width: specifics.strokeWidth || 3,
        };
        if (type === "area") {
            options.fill = { opacity: specifics.fillOpacity || 0.35 };
        }
        break;
      }
      case "pie":
      case "donut": {
        options.series = data.series[0]?.data || [];
        options.labels = data.labels;
        options.plotOptions = {
          pie: {
            donut: { size: specifics.innerRadius ? `${specifics.innerRadius}%` : "65%" },
          },
        };
        break;
      }
      case "radar": {
        options.series = data.series;
        options.xaxis = {
          categories: data.labels,
          labels: { style: { colors: textColor } },
        };
        options.yaxis = { show: false };
        options.fill = { opacity: specifics.fillOpacity || 0.3 };
        break;
      }
      case "scatter": {
        options.series = data.series;
        options.xaxis = {
          labels: { style: { colors: textColor } },
          tickAmount: 6,
        };
        options.yaxis = {
          labels: { style: { colors: textColor } },
        };
        break;
      }
      case "heatmap": {
        options.series = data.series.map((s: any) => ({
          name: s.name,
          data: data.labels.map((l: string, i: number) => ({ x: l, y: s.data[i] || 0 })),
        }));
        options.colors = [colors[0] || "#2563EB"];
        options.plotOptions = {
          heatmap: {
            shadeIntensity: 0.5,
            colorScale: { ranges: [
              { from: 0, to: 15, color: "#E8F0FE", name: "Low" },
              { from: 16, to: 30, color: "#1A73E8", name: "Medium" },
              { from: 31, to: 50, color: "#0D47A1", name: "High" },
            ]},
          },
        };
        break;
      }
      case "treemap": {
        options.series = [{
          data: data.labels.map((l: string, i: number) => ({ x: l, y: data.series[0]?.data[i] || 0 })),
        }];
        break;
      }
      case "funnel": {
        options.series = [{
            name: "Funnel",
            data: data.series[0]?.data || [],
        }];
        options.labels = data.labels;
        options.plotOptions = {
            bar: {
                borderRadius: 0,
                horizontal: true,
                barHeight: "80%",
                isFunnel: true,
            },
        };
        options.dataLabels = {
            enabled: true,
            formatter: function (val: any, opt: any) {
                return opt.w.globals.labels[opt.dataPointIndex] + ":  " + val;
            },
            dropShadow: { enabled: true },
        };
        break;
      }
      case "radialBar": {
        options.series = data.series[0]?.data.slice(0, 1) || [0];
        options.labels = data.labels.slice(0, 1) || ["Progress"];
        options.plotOptions = {
          radialBar: {
            hollow: { size: "40%" },
            dataLabels: {
              name: { fontSize: "13px", color: textColor },
              value: { fontSize: "20px", fontWeight: 700, color: textColor },
            },
          },
        };
        break;
      }
    }
}

function getApexType(type: string): string {
  const map: Record<string, string> = {
    bar: "bar", line: "line", area: "area", pie: "pie", donut: "donut",
    radar: "radar", scatter: "scatter", heatmap: "heatmap", treemap: "treemap",
    funnel: "bar", radialBar: "radialBar",
  };
  return map[type] || "bar";
}

export default ChartRenderer;
